import './style.modules.scss'

import { Spacer } from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import React, { useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { ArticleHeroPillarVariant } from './ArticleHeroPillarVariant.type'
import { PillarBoxBackgroundColor } from './PillarBoxColor.type'

export type ArticleHeroPillarProps = {
  header: string
  description?: string
  image: string
  imageMobile?: string
  alt: string
  boxColor?: PillarBoxBackgroundColor
  headerColor?: 'white' | 'neutral' | string
  variant?: string
  componentSpacing?: SpacerSpacing
  testId?: string
}

export const ArticleHeroPillar = (props: ArticleHeroPillarProps) => {
  const {
    header,
    description,
    image,
    imageMobile,
    alt,
    boxColor,
    headerColor,
    variant,
    componentSpacing,
    testId
  } = props
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const [marginLeft, setMarginLeft] = useState(0)
  const [imgWidth, setImgWidth] = useState(0)
  const bgColorDivRef = useRef<HTMLDivElement>(null)

  const heroBoxClassNames = ['ArticleHeroPillar-box u-flex']
  const headerClassNames = ['ArticleHeroPillar-heading']
  let backgroundColor: string

  useEffect(() => {
    const headingElement = headingRef.current
    updateMargin()
    window.addEventListener('resize', updateMargin)

    if (!headingElement) return

    const resizeObserver = new ResizeObserver(() => {
      const height = headingElement.clientHeight
      const browserWidth = window.innerWidth
      if (
        variant == ArticleHeroPillarVariant.SIMPLE_HERO &&
        height > 209 &&
        browserWidth > 1024
      ) {
        headingElement.classList.remove('Type-Display')
      }
    })
    resizeObserver.observe(headingElement)

    return () => {
      window.removeEventListener('resize', updateMargin)
      resizeObserver.disconnect()
    }
  })

  const updateMargin = () => {
    if (bgColorDivRef.current) {
      setMarginLeft(bgColorDivRef.current.offsetWidth - 115)
      setImgWidth(bgColorDivRef.current.offsetWidth - 115)
    }
  }

  if (variant == ArticleHeroPillarVariant.HERO_PILLAR) {
    heroBoxClassNames.push(
      'u-paddingTop32 u-paddingBottom32 u-lg-size5of12 u-md-size5of12 u-paddingLeft20 u-paddingRight20'
    )
  } else {
    heroBoxClassNames.push('ArticleHeroPillar-box--simple')
    headerClassNames.push('ArticleHeroPillar-heading--simple Type-Display')
  }

  switch (boxColor) {
    case BackgroundColor.DARK_MINT:
      heroBoxClassNames.push('u-bgColorDarkMint')
      backgroundColor =
        'linear-gradient(90deg, rgba(222, 238, 239, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.BERRY:
      heroBoxClassNames.push('u-bgColorBerry')
      backgroundColor =
        'linear-gradient(90deg, rgba(243, 242, 241, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.MINT:
      heroBoxClassNames.push('u-bgColorMint')
      backgroundColor =
        'linear-gradient(90deg, rgba(222, 238, 239, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.MIDNIGHT_BLUE:
      heroBoxClassNames.push('u-bgColorMidnightBlue')
      backgroundColor =
        'linear-gradient(90deg, rgba(243, 242, 241, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.ORANGE:
      heroBoxClassNames.push('u-bgColorOrange')
      backgroundColor =
        'linear-gradient(90deg, rgba(243, 242, 241, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.LIME_LIGHT:
      heroBoxClassNames.push('u-bgColorLime')
      backgroundColor =
        'linear-gradient(90deg, rgba(242, 247, 221, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.LIGHT_BLUE:
      heroBoxClassNames.push('u-bgColorLightBlue')
      backgroundColor =
        'linear-gradient(90deg, rgba(243, 242, 241, 1) 77%, rgba(247, 245, 248, 0) 100%)'
      break
    case BackgroundColor.DARK_GREY:
      heroBoxClassNames.push('u-bgColorDarkGrey')
      backgroundColor = 'none'
      break
    case BackgroundColor.PURPLE:
    default:
      heroBoxClassNames.push('u-bgColorPurple')
      backgroundColor =
        'linear-gradient(90deg, rgba(235, 232, 238, 1) 77%, rgba(247, 245, 248, 0) 100%)'
  }

  switch (headerColor) {
    case 'neutral':
      headerClassNames.push('u-textColorNeutral')
      break
    case 'white':
    default:
      headerClassNames.push('u-textColorWhite')
  }

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className={`ArticleHeroPillar u-sizeConstrained ${
          variant == ArticleHeroPillarVariant.SIMPLE_HERO
            ? 'u-sm-sizeConstrainedFlush'
            : ''
        }`}
      >
        <div
          className={`ArticleHeroPillar-container ${
            variant == ArticleHeroPillarVariant.SIMPLE_HERO
              ? 'ArticleHeroPillar-container--simple u-md-flex u-md-flexColReverse'
              : ''
          } u-posRelative u-paddingBottom20 u-sm-flex u-sm-flexColReverse`}
        >
          <div
            className={heroBoxClassNames.join(' ')}
            data-testid={testId ? `${testId}-box` : null}
          >
            <h1
              className={headerClassNames.join(' ')}
              ref={headingRef}
              data-testid={testId ? `${testId}-box-header` : null}
              dangerouslySetInnerHTML={{ __html: xss(header) }}
            ></h1>
          </div>
          {variant == ArticleHeroPillarVariant.HERO_PILLAR && (
            <div
              className="ArticleHeroPillar-background u-flex u-lg-size5of12 u-md-size5of12 u-sm-hidden"
              ref={bgColorDivRef}
              style={{ background: backgroundColor }}
            ></div>
          )}
          <div
            className={`ArticleHeroPillar-imageContainer ${
              variant == ArticleHeroPillarVariant.SIMPLE_HERO
                ? 'ArticleHeroPillar-image--simple'
                : 'u-sm-hidden'
            }`}
            style={{
              marginLeft: `${marginLeft}px`,
              width: `calc(100% - ${imgWidth}px)`
            }}
          >
            <img
              className="ArticleHeroPillar-featuredImage u-sizeFull u-sm-hidden"
              src={image}
              alt={alt ?? ''}
              data-testid={testId ? `${testId}-image` : null}
            />
            {imageMobile && (
              <img
                className="ArticleHeroPillar-featuredImage u-sizeFull u-lg-hidden u-md-hidden"
                src={imageMobile}
                alt={alt ?? ''}
                data-testid={testId ? `${testId}-image` : null}
              />
            )}
          </div>
          {variant == ArticleHeroPillarVariant.HERO_PILLAR && (
            <img
              className="ArticleHeroPillar-mobileFeaturedImage u-lg-hidden u-md-hidden"
              src={image}
              alt={alt ?? ''}
            />
          )}
        </div>
        {description && variant == ArticleHeroPillarVariant.HERO_PILLAR && (
          <div
            className="ArticleHeroPillar-description u-lg-size8of12 u-md-size8of12 u-paddingBottom20"
            dangerouslySetInnerHTML={{ __html: xss(description) }}
          ></div>
        )}
      </section>
    </Spacer>
  )
}
